/**
 * @license
 * Copyright (c) 2004 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { NativeDateTimeResolvedOptions } from './NativeDateTimeOptions';

/**
 * Date time utilities.
 */

const regexTrim = /^\s+|\s+$|\u200f|\u200e/g;
const regexTrimNumber = /\s+|\u200f|\u200e/g;
const regexTrimRightZeros = /0+$/g;
const zeros = ['0', '00', '000'];

// ISO 8601 string accepted values:
// -date only: YYYY or YYYY-MM or YYYY-MM-dd
// -time only without timezone: Thh:mm or Thh:mm:ss or Thh:mm:ss.SSS
// -time only with timezone: any of the time values above followed by any of the following:
// Z or +/-hh:mm or +/-hhmm or +/-hh
// -date time: any of the date values followed by any of the time values
export const _ISO_DATE_REGEXP =
  /^[+-]?\d{4}(?:-\d{2}(?:-\d{2})?)?(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}(?::?\d{2})?)?)?$|^T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}(?::?\d{2})?)?$/;

const _ISO_DATE_ONLY_REGEXP = /^[+-]?\d{4}-\d{2}-\d{2}$/;

/**
 * Returns true if the value represents a date-only ISO string.
 *
 * @param {string} value
 * @returns true if the string represents a date-only ISO string
 */
export function isDateOnlyIsoString(value?: string | null) {
  // If value is undefined/null, returns true.
  // If value is empty or blank string, returns false.
  // Otherwise checks value against the regexp.
  const isValid =
    value === undefined || value === null
      ? true
      : value.trim().length > 0
      ? _ISO_DATE_ONLY_REGEXP.test(value.trim())
      : false;
  return isValid;
}

export function startsWith(value: string, pattern: string) {
  return value.indexOf(pattern) === 0;
}

export function trim(value: string) {
  return (value + '').replace(regexTrim, '');
}

export function trimRightZeros(value: string) {
  return (value + '').replace(regexTrimRightZeros, '');
}

export function trimNumber(value: string) {
  const s = (value + '').replace(regexTrimNumber, '');
  return s;
}

export function toUpper(value: string) {
  // "he-IL" has non-breaking space in weekday names.
  return value.split('\u00A0').join(' ').toUpperCase();
}

export function padZeros(num: number, c: number) {
  let s = num + '';
  let isNegative = false;
  if (num < 0) {
    s = s.substr(1);
    isNegative = true;
  }
  if (c > 1 && s.length < c) {
    s = zeros[c - 2] + s;
    s = s.substr(s.length - c, c);
  }
  if (isNegative) {
    s = '-' + s;
  }
  return s;
}

export function zeroPad(str: string, count: number, left: boolean) {
  let result = '' + str;

  for (let l = result.length; l < count; l += 1) {
    result = left ? '0' + result : result + '0';
  }
  return result;
}

function _throwInvalidISOStringRange(
  isoStr: string,
  name: string,
  displayValue: string | number,
  displayLow: number,
  displayHigh: number
) {
  const msg =
    'The string ' +
    isoStr +
    ' is not a valid ISO 8601 string: ' +
    displayValue +
    ' is out of range.  Enter a value between ' +
    displayLow +
    ' and ' +
    displayHigh +
    ' for ' +
    name;

  const errorInfo = {
    cause: {
      code: 'isoStringOutOfRange',
      parameterMap: {
        isoStr: isoStr,
        value: displayValue,
        minValue: displayLow,
        maxValue: displayHigh,
        propertyName: name
      }
    }
  };

  const rangeError = new (RangeError as any)(msg, errorInfo);
  throw rangeError;
}

function _throwInvalidISOStringSyntax(str: string) {
  const msg = 'The string ' + str + ' is not a valid ISO 8601 string syntax.';
  const errorInfo = {
    cause: {
      code: 'invalidISOString',
      parameterMap: {
        isoStr: str
      }
    }
  };
  const e = new (Error as any)(msg, errorInfo);
  throw e;
}

/**
 * <p>Returns a local ISO string provided a Date object.
 * This method can be used to convert a moment in time or a local ISO string into
 * a local ISO string. It can also be used to convert any Date object into local ISO
 * string.</p>
 * Examples below are where the local (user's system) time zone is UTC-06:00<br>
 * dateToLocalIso(new Date('2021-06-04T00:00:00-04:00')); -->'2021-06-03T22:00:00'<br>
 * dateToLocalIso(new Date('2021-06-04T02:30:00Z')); -->'2021-06-03T20:30:00'<br>
 * In this example the input ISO string is local, the output is the same:<br>
 * dateToLocalIso(new Date('2021-06-04T02:30:00')); -->'2021-06-04T02:30:00'<br>
 * In this example we just want to convert a Date object:<br>
 * dateToLocalIso(new Date());<br>
 */
export function dateToLocalIso(_date: Date | number) {
  let date = _date;
  if (typeof date === 'number') {
    date = new Date(date);
  }
  let isoStr =
    padZeros(date.getFullYear(), 4) +
    '-' +
    padZeros(date.getMonth() + 1, 2) +
    '-' +
    padZeros(date.getDate(), 2) +
    'T' +
    padZeros(date.getHours(), 2) +
    ':' +
    padZeros(date.getMinutes(), 2) +
    ':' +
    padZeros(date.getSeconds(), 2);
  if (date.getMilliseconds() > 0) {
    isoStr += '.' + trimRightZeros(padZeros(date.getMilliseconds(), 3));
  }
  return isoStr;
}

export function isLeapYear(y: number) {
  if (y % 400 === 0) {
    return true;
  } else if (y % 100 === 0) {
    return false;
  } else if (y % 4 === 0) {
    return true;
  }
  return false;
}

// Get days in month depending on month and leap year
export function getDaysInMonth(y: number, m: number) {
  switch (m) {
    case 0:
    case 2:
    case 4:
    case 6:
    case 7:
    case 9:
    case 11:
      return 31;
    case 1:
      if (isLeapYear(y)) {
        return 29;
      }
      return 28;
    default:
      return 30;
  }
}

export function IsoStrParts(isoString: string) {
  const tst = _ISO_DATE_REGEXP.test(isoString);
  if (tst === false) {
    _throwInvalidISOStringSyntax(isoString);
  }
  const splitted = isoString.split('T');
  const tIndex = isoString.indexOf('T');
  const today = new Date();
  let i;
  let isBC = false;
  const datetime = [today.getFullYear(), today.getMonth() + 1, today.getDate(), 0, 0, 0, 0];

  if (splitted[0] !== '') {
    // contains date portion
    // test if date is BC, year is negative
    if (startsWith(splitted[0], '-')) {
      splitted[0] = splitted[0].slice(1);
      isBC = true;
    }
    const dateSplitted = splitted[0].split('-');
    for (i = 0; i < dateSplitted.length; i++) {
      const val = parseInt(dateSplitted[i], 10);
      // validate month
      if (i === 1) {
        if (val < 1 || val > 12) {
          _throwInvalidISOStringRange(isoString, 'month', val, 1, 12);
        }
      }
      // validate day
      if (i === 2) {
        const nbDays = getDaysInMonth(datetime[0], datetime[1] - 1);
        if (val < 1 || val > nbDays) {
          _throwInvalidISOStringRange(isoString, 'day', val, 1, nbDays);
        }
      }
      datetime[i] = val;
    }
    if (isBC) {
      datetime[0] = -datetime[0];
    }
  }

  if (tIndex !== -1) {
    const milliSecSplitted = splitted[1].split('.'); // contain millseconds
    const timeSplitted = milliSecSplitted[0].split(':'); // contain hours, minutes, seconds

    for (i = 0; i < timeSplitted.length; i++) {
      const tVal = parseInt(timeSplitted[i], 10);
      // validate hour
      if (i === 0) {
        if (tVal < 0 || tVal > 24) {
          _throwInvalidISOStringRange(isoString, 'hour', tVal, 0, 24);
        }
      }
      // validate minute
      if (i === 1) {
        if (tVal < 0 || tVal > 59) {
          _throwInvalidISOStringRange(isoString, 'minute', tVal, 0, 59);
        }
      }
      // validate second
      if (i === 2) {
        if (tVal < 0 || tVal > 59) {
          _throwInvalidISOStringRange(isoString, 'second', tVal, 0, 59);
        }
      }
      datetime[3 + i] = tVal;
    }

    if (milliSecSplitted.length === 2 && milliSecSplitted[1]) {
      datetime[6] = parseInt(zeroPad(milliSecSplitted[1], 3, false), 10);
    }
  }
  return datetime;
}

function _toBoolean(value: any) {
  if (typeof value === 'string') {
    const s = value.toLowerCase().trim();
    switch (s) {
      case 'true':
      case '1':
        return true;
      case 'false':
      case '0':
        return false;
      default:
        return value;
    }
  }
  return value;
}

// Return a function getOption.
// The getOption function extracts the value of the property named
// property from the provided options object, converts it to the required type,
// checks whether it is one of a List of allowed values, and fills in a
// fallback value if necessary.
export function getGetOption(options: NativeDateTimeResolvedOptions, getOptionCaller: string) {
  if (options === undefined) {
    throw new Error('Internal ' + getOptionCaller + ' error. Default options missing.');
  }

  const getOption = function (property: any, type: any, values: any, defaultValue: any) {
    if ((options as any)[property] !== undefined) {
      let value = (options as any)[property];
      switch (type) {
        case 'boolean':
          value = _toBoolean(value);
          break;
        case 'string':
          value = String(value);
          break;
        case 'number':
          value = Number(value);
          break;
        default:
          throw new Error('Internal error. Wrong value type.');
      }
      if (values !== undefined && values.indexOf(value) === -1) {
        const expectedValues = [];
        for (let i = 0; i < values.length; i++) {
          expectedValues.push(values[i]);
        }
        const msg =
          "The value '" +
          (options as any)[property] +
          "' is out of range for '" +
          getOptionCaller +
          "' options property '" +
          property +
          "'. Valid values: " +
          expectedValues;
        const rangeError = new RangeError(msg);
        const errorInfo = {
          errorCode: 'optionOutOfRange',
          parameterMap: {
            propertyName: property,
            propertyValue: (options as any)[property],
            propertyValueValid: expectedValues,
            caller: getOptionCaller
          }
        };
        (rangeError as any).errorInfo = errorInfo;
        throw rangeError;
      }

      return value;
    }
    return defaultValue;
  };

  return getOption;
}

export function partsToIsoString(parts: any) {
  let isoStr =
    padZeros(parts[0], 4) +
    '-' +
    padZeros(parts[1], 2) +
    '-' +
    padZeros(parts[2], 2) +
    'T' +
    padZeros(parts[3], 2) +
    ':' +
    padZeros(parts[4], 2) +
    ':' +
    padZeros(parts[5], 2);
  if (parts[6] > 0) {
    isoStr += '.' + trimRightZeros(padZeros(parts[6], 3));
  }
  return isoStr;
}

/**
 * Returns a local Date object from a local ISO string. This method is only meant
 * to work with local ISO strings. If the input ISO string contain Z or offset,
 * they will be ignored.
 * @param {string} isoString
 * @memberof oj.OraI18nUtils
 * @method isoToLocalDate
 */
export function isoToLocalDate(isoString: string) {
  if (!isoString || typeof isoString !== 'string') {
    return null;
  }
  return _isoToLocalDateIgnoreTimezone(isoString);
}

function _isoToLocalDateIgnoreTimezone(isoString: string) {
  const datetime = IsoStrParts(isoString);
  const date = new Date(
    datetime[0],
    datetime[1] - 1,
    datetime[2],
    datetime[3],
    datetime[4],
    datetime[5],
    datetime[6]
  );
  // As per the documentation:
  // new Date(year, monthIndex [, day [, hours [, minutes [, seconds [, milliseconds]]]]]);
  // year - Integer value representing the year and
  // values from 0 to 99 map to the years 1900 to 1999; all other values are the actual year.
  // Use Date.prototype.setFullYear to create dates between the years 0 and 99.
  date.setFullYear(datetime[0]);
  return date;
}

export function getISOStrFormatInfo(isoStr: string) {
  const res: any = {
    format: null,
    dateTime: null,
    timeZone: '',
    isoStrParts: null
  };
  const exe: any = _ISO_DATE_REGEXP.exec(isoStr);

  if (exe === null) {
    _throwInvalidISOStringSyntax(isoStr);
  }
  if (exe && exe[1] === undefined && exe[2] === undefined) {
    res.format = 'local';
    res.dateTime = isoStr;
    res.isoStrParts = IsoStrParts(res.dateTime);
    return res;
  }
  res.timeZone = exe[1] !== undefined ? exe[1] : exe[2];
  if (res.timeZone === 'Z') {
    res.format = 'zulu';
  } else {
    res.format = 'offset';
  }
  const isoStrLen = isoStr.length;
  const timeZoneLen = res.timeZone.length;
  res.dateTime = isoStr.substring(0, isoStrLen - timeZoneLen);
  res.isoStrParts = IsoStrParts(res.dateTime);
  return res;
}

export function getTimeStringFromOffset(
  prefix: string,
  offset: number,
  reverseSign: boolean,
  alwaysMinutes: boolean
) {
  const isNegative = reverseSign ? offset > 0 : offset < 0;
  const absOffset = Math.abs(offset);
  let hours: any = Math.floor(absOffset / 60);
  const minutes: any = absOffset % 60;
  const sign = isNegative ? '-' : '+';

  if (alwaysMinutes) {
    hours = zeroPad(hours, 2, true);
  }
  let str = prefix + sign + hours;
  if (minutes > 0 || alwaysMinutes) {
    str += ':' + zeroPad(minutes, 2, true);
  }
  return str;
}
